/* eslint-disable no-unused-expressions */
import * as Yup from 'yup';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

// auth action
import { toast } from 'react-toastify';
import { addNewUser, updateUser } from '../../store/actions/users';

// components
import { createNewProduct, getAppSetting, getProcessorProduct, getProcessors } from '../../api/services';
import { BRAND_IMAGES } from '../../utils/assets';

// ----------------------------------------------------------------------
export default function NewDataProductForm({ sentValue, update }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { services } = useSelector((state) => state.services);
  const [dataPlans, setDataPlan] = useState([]);

  const [planNames, setPlanNames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [planCategory, setPlanCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [buyingAmount, setBuyingAmount] = useState('');
  const [vipAmount, setVipAmount] = useState('');
  const [isAirtimeSubService, setIsAirtimeSubService] = useState(false);
  const [cashback, setCashback] = useState('');
  const [referralBonus, setReferralBonus] = useState('');

  const [name, setName] = useState('');
  const [createPlanFrom, setCreatePlanFrom] = useState('name');

  const [planId, setPlanId] = useState('');
  const [planNetwork, setPlanNetwork] = useState('');

  const [processors, setProcessors] = useState([]);
  const [identifier, setIdentifier] = useState('');
  const [processor, setProcessor] = useState('');

  const [currentProcessors, setCurrentProcessors] = useState([]);

  const [validity, setValidity] = useState('');
  const [descriptions, setDescriptions] = useState('');

  const [valid, setValid] = useState(false);

  const { isLoading, added } = useSelector((state) => state.users);
  const [loading, setLoading] = useState(false);

  const NewUserSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    first_name: Yup.string().required('First Name is required'),
    last_name: Yup.string().required('Last Name is required'),
    phone_number: Yup.string().required('phone number is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = sentValue || {
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    update ? dispatch(updateUser(data)) : dispatch(addNewUser(data));
  };

  const handleGetDataPlans = useCallback(async (processor) => {
    const request = await getProcessorProduct(processor);
    if (request.ok) {
      setDataPlan(request.data?.data?.dataplans || []);
    }
  }, []);

  React.useEffect(() => {
    if (added) {
      navigate('/dashboard/user');
    }
  }, [added, navigate]);

  React.useEffect(() => {
    handleGetDataPlans(processor);
  }, [handleGetDataPlans, processor]);

  const [network, setNetwork] = React.useState('mtn');

  const handleChange = (event) => {
    setNetwork(event.target.value);
  };

  const handleGenerate = useCallback(() => {
    if (dataPlans.length < 1 || services?.length < 1) {
      return;
    }

    const filterListCategory = [];
    const filterProductList = [];
    const createdPlanName = [];

    services.forEach((element) => {
      if (element?.service === 'Data' && element?.category.toLowerCase().includes(network)) {
        filterProductList.push(element);
        createdPlanName.push(element.details?.planName);
        if (!filterListCategory.includes(element.category)) {
          filterListCategory.push(element.category);
        }
      }
    });

    const availablePlanName = dataPlans.filter(
      (data) => data.planname.includes(network) && !createdPlanName.includes(data.planname)
    );

    setPlanNames(availablePlanName);
    setCategories(filterListCategory);
  }, [dataPlans, network, services]);

  useEffect(() => {
    handleGenerate();
  }, [network, dataPlans, services, handleGenerate]);

  useEffect(() => {
    if (createPlanFrom === 'name') {
      if (planCategory.length < 2 || name.length < 3 || !amount || !selectedPlanId) {
        setValid(false);
        return;
      }
    }

    if (createPlanFrom === 'id') {
      if (planCategory.length < 2 || name.length < 3 || !amount || !planId || !planNetwork) {
        setValid(false);
        return;
      }
    }

    setValid(true);
  }, [planCategory, name, amount, selectedPlanId, createPlanFrom, planId, planNetwork]);

  const handleCreatePlan = async () => {
    let normalizePlanCategory = planCategory;
    // Check and replace
    if (!planCategory?.trim()?.toLowerCase().startsWith(`${network} `)) {
      if (planCategory?.trim()?.toLowerCase().startsWith(network)) {
        normalizePlanCategory = planCategory?.trim()?.toLowerCase().replace(network, `${network} `);
      } else {
        normalizePlanCategory = `${network} ${planCategory?.trim()?.toLowerCase()}`;
      }
    }

    const requestPayload = {
      service: 'Data',
      category: normalizePlanCategory.toUpperCase(),
      amount: Number(amount),
      buyingAmount: Number(buyingAmount),
      vipAmount: Number(vipAmount),
      isAirtimeSubService,
      cashback: Number(cashback),
      referralBonus: Number(referralBonus),
      imageUrl: BRAND_IMAGES[network?.toLowerCase()],
      validity,
      descriptions,
      details: {
        name: name?.trim(),
        amount,
        planName: selectedPlanId,
        custom: createPlanFrom === 'id',
      },
      customData: createPlanFrom === 'id' ? { networkid: planNetwork, planid: planId } : null,
    };

    setLoading(true);

    const request = await createNewProduct(requestPayload, identifier);
    if (request.ok) {
      setLoading(false);
      toast.success('Data Plan Added successfully');
      navigate('/dashboard/services');
      return;
    }

    setLoading(false);
    toast.error(request.data ? request.data.message : 'Error while adding data plan');
  };

  const handleGetProcessors = useCallback(async () => {
    const response = await getProcessors();

    if (response.ok) {
      setProcessors(response.data.data);
      if (response.data?.data?.length > 0) {
        setProcessor(response.data?.data[0]);
      }
    }
  }, []);

  const handleGetAppSetting = useCallback(async () => {
    const request = await getAppSetting();
    if (request.ok) {
      const { data } = request.data;
      setCurrentProcessors(data?.service_processor?.current_processors || []);
    }
  }, []);

  useEffect(() => {
    handleGetProcessors();
    handleGetAppSetting();
  }, [handleGetAppSetting, handleGetProcessors]);

  return (
    // <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
    <Stack sx={{}} spacing={2} maxWidth={450}>
      <FormControl>
        <InputLabel>Processor</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={identifier}
          label="Processor"
          onChange={(e) => {
            setIdentifier(e.target.value);
            setProcessor(currentProcessors.find((item) => item.identifier === e.target.value)?.name);
          }}
        >
          {currentProcessors?.map((pr) => (
            <MenuItem key={pr} value={pr.identifier}>
              {pr.name} - {pr.identifier}
            </MenuItem>
          ))}
        </Select>
        <Typography>Please select the processor you wish to create the data plan in</Typography>
      </FormControl>
      <FormControl>
        <InputLabel>Network</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={network}
          label="Network"
          onChange={handleChange}
        >
          <MenuItem value={'mtn'}>MTN</MenuItem>
          <MenuItem value={'9mobile'}>9MOBILE</MenuItem>
          <MenuItem value={'airtel'}>AIRTEL</MenuItem>
          <MenuItem value={'glo'}>GLO</MenuItem>
          <MenuItem value={'smile'}>SMILE</MenuItem>
          <MenuItem value={'kirani'}>KIRANI</MenuItem>
          <MenuItem value={'alpha'}>ALPHA</MenuItem>
          <MenuItem value={'ratel'}>RATEL</MenuItem>
        </Select>
      </FormControl>
      <FormControl>
        <InputLabel>Category</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={selectedCategory}
          label="Category"
          onChange={(e) => {
            if (e.target.value !== 'new') {
              setPlanCategory(e.target.value);
              setSelectedCategory(e.target.value);
              return;
            }
            setSelectedCategory(e.target.value);
            setPlanCategory('');
          }}
        >
          {categories.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
          <MenuItem value={'new'}>Create new</MenuItem>
        </Select>
      </FormControl>
      {selectedCategory === 'new' && (
        <TextField
          name="category"
          value={planCategory}
          onChange={(e) => {
            const { value } = e.target;
            // Only allow alphanumeric characters (letters and numbers)
            if (/^[a-zA-Z0-9\s]*$/.test(value)) {
              setPlanCategory(value);
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            return false;
          }}
          onCopy={(e) => {
            e.preventDefault();
            return false;
          }}
          label="New Category Name"
          helperText="Category should start with the network name"
        />
      )}
      <TextField
        name="planName"
        value={name}
        label="Name"
        onChange={(e) => {
          const { value } = e.target;
          // Only allow alphanumeric characters (letters and numbers)
          if (/^[a-zA-Z0-9\s.]*$/.test(value)) {
            setName(value);
          }
        }}
      />
      <TextField
        name="amount"
        type="number"
        label="Amount"
        value={amount}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
        onCopy={(e) => {
          e.preventDefault();
          return false;
        }}
        onChange={(e) => {
          if (Number.isNaN(amount)) {
            return;
          }
          setAmount(e.target.value);
        }}
      />
      <TextField
        name="buyingAmount"
        type="number"
        label="Buying Price"
        value={buyingAmount}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
        onCopy={(e) => {
          e.preventDefault();
          return false;
        }}
        onChange={(e) => {
          if (Number.isNaN(buyingAmount)) {
            return;
          }
          setBuyingAmount(e.target.value);
        }}
      />
      <TextField
        name="vipAmount"
        type="number"
        label="Vip Amount"
        value={vipAmount}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
        onCopy={(e) => {
          e.preventDefault();
          return false;
        }}
        onChange={(e) => {
          if (Number.isNaN(vipAmount)) {
            return;
          }
          setVipAmount(e.target.value);
        }}
      />
      <TextField
        name="cashback"
        type="number"
        label="Cashback"
        value={cashback}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
        onCopy={(e) => {
          e.preventDefault();
          return false;
        }}
        onChange={(e) => {
          if (Number.isNaN(cashback)) {
            return;
          }
          setCashback(e.target.value);
        }}
      />
      <TextField
        name="referralBonus"
        type="number"
        label="Referral Bonus"
        value={referralBonus}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
        onCopy={(e) => {
          e.preventDefault();
          return false;
        }}
        onChange={(e) => {
          if (Number.isNaN(referralBonus)) {
            return;
          }
          setReferralBonus(e.target.value);
        }}
      />
      <FormControl>
        <InputLabel>Create from</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={createPlanFrom}
          label="Plan ID"
          onChange={(e) => setCreatePlanFrom(e.target.value)}
        >
          <MenuItem value="name">Plan ID Name</MenuItem>
          <MenuItem value="id">Plan ID</MenuItem>
        </Select>
      </FormControl>
      {createPlanFrom === 'id' ? (
        <>
          <TextField
            label="Plan ID"
            value={planId}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
            onChange={(e) => {
              setPlanId(e.target.value);
            }}
          />
          <TextField
            label="Network ID"
            value={planNetwork}
            onPaste={(e) => {
              e.preventDefault();
              return false;
            }}
            onCopy={(e) => {
              e.preventDefault();
              return false;
            }}
            onChange={(e) => {
              setPlanNetwork(e.target.value);
            }}
          />
        </>
      ) : (
        <FormControl>
          <InputLabel>Plan ID Name</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={selectedPlanId}
            label="Plan ID"
            onChange={(e) => setSelectedPlanId(e.target.value)}
          >
            {planNames.map((plan) => (
              <MenuItem key={plan.id} value={plan.planname}>
                {plan.planname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <TextField
        name="validity"
        label="Validity"
        value={validity}
        onChange={(e) => {
          setValidity(e.target.value);
        }}
      />

      <TextField
        name="description"
        label="Description"
        multiline
        minRows={3}
        value={descriptions}
        onChange={(e) => {
          setDescriptions(e.target.value);
        }}
      />

      {/* Show airtime sub service switch only if it is data category */}

      <FormControlLabel
        control={<Switch checked={isAirtimeSubService} onChange={(e) => setIsAirtimeSubService(e.target.checked)} />}
        label="Airtime Sub Service"
      />

      <LoadingButton
        size="large"
        type="submit"
        variant="contained"
        onClick={handleCreatePlan}
        loading={loading}
        disabled={!valid}
      >
        Create new Data plan
      </LoadingButton>
    </Stack>
  );
}
